// Island model: the hand-designed map of PRD section 4 as plain data.
// Pure TypeScript with no DOM or three.js, so the simulation (src/sim) shares it.

export type TerrainCode = 'p' | 'b' | 'a' | 'h' | 'c' | 'm' | 't' | 'l' | 'r';
export type Side = 'N' | 'S' | 'E' | 'W';
export type PortKind = 'in' | 'out';
export type TileKind = 'mina' | 'fundicion' | 'taller' | 'lab' | 'bomba' | 'muelle';
export type RouteKind = 'road' | 'rail' | 'barge' | 'boat' | 'drone';
export type Cell = readonly [col: number, row: number];
/** Materials named as in the design system's ResourceIcon. */
export type ResourceKind = 'mineral' | 'hierro' | 'circuito' | 'ciencia' | 'agua' | 'engranaje';
export type Point = readonly [x: number, z: number];

export interface TerrainInfo {
  /** Spanish name shown to players. */
  readonly name: string;
  /** Height of the tile top, in tile units (PRD section 14.3). */
  readonly top: number;
  readonly water: boolean;
}

/** Terrain codes of the mockup data and their heights (PRD section 14.3). */
export const TERRAIN: Readonly<Record<TerrainCode, TerrainInfo>> = {
  p: { name: 'Prado', top: 0, water: false },
  b: { name: 'Bosque', top: 0.04, water: false },
  a: { name: 'Arena', top: -0.07, water: false },
  h: { name: 'Roca con hierro', top: 0.14, water: false },
  c: { name: 'Roca con cobre', top: 0.14, water: false },
  m: { name: 'Montaña', top: 0.14, water: false },
  t: { name: 'Roca termal', top: 0.06, water: false },
  l: { name: 'Lago', top: -0.26, water: true },
  r: { name: 'Río', top: -0.26, water: true },
};

/** Sea level in world units; tile tops sit above it. */
export const SEA_Y = -0.36;

export interface Port {
  readonly cell: Cell;
  readonly side: Side;
  readonly kind: PortKind;
}

export interface Tile {
  readonly id: string;
  readonly kind: TileKind;
  readonly name: string;
  readonly cells: readonly Cell[];
  readonly ore?: 'iron' | 'copper';
  readonly product: string;
  readonly ports: readonly Port[];
}

export interface Route {
  readonly kind: RouteKind;
  readonly name: string;
  /** Polyline in grid units (column, row), or a quadratic curve with three control points. */
  readonly pts?: readonly Point[];
  readonly curve?: readonly [Point, Point, Point];
  /** Where the mockup placed the vehicle along the route, from 0 to 1. */
  readonly at: number;
}

export interface Island {
  readonly cols: number;
  readonly rows: number;
  readonly terrain: ReadonlyMap<string, TerrainCode>;
  readonly tiles: readonly Tile[];
  readonly routes: readonly Route[];
  readonly fog: ReadonlySet<string>;
  readonly locks: ReadonlyMap<string, number>;
  readonly buyTarget: string;
  readonly goal: Cell;
}

export const cellKey = (col: number, row: number): string => `${col},${row}`;

export function parseKey(key: string): Cell {
  const [c, r] = key.split(',').map(Number);
  if (c === undefined || r === undefined || !Number.isInteger(c) || !Number.isInteger(r)) {
    throw new Error(`Clave de casilla no válida: ${key}`);
  }
  return [c, r];
}

/** World x of a column centre: the island is centred on the origin. */
export const worldX = (island: Island, col: number): number => col - island.cols / 2 + 0.5;
/** World z of a row centre. */
export const worldZ = (island: Island, row: number): number => row - island.rows / 2 + 0.5;

export function terrainAt(island: Island, col: number, row: number): TerrainCode | undefined {
  return island.terrain.get(cellKey(col, row));
}

/** Height of the ground at a world position, or sea level outside the island. */
export function topAt(island: Island, x: number, z: number): number {
  const code = terrainAt(island, Math.floor(x + island.cols / 2), Math.floor(z + island.rows / 2));
  return code ? TERRAIN[code].top : SEA_Y;
}

export function ownedCells(island: Island): Map<string, Tile> {
  const owned = new Map<string, Tile>();
  for (const tile of island.tiles) for (const [c, r] of tile.cells) owned.set(cellKey(c, r), tile);
  return owned;
}

// ------------------------------------------------------------ parsing ---

const TERRAIN_CODES = new Set(Object.keys(TERRAIN));
const SIDES = new Set(['N', 'S', 'E', 'W']);
const TILE_KINDS = new Set(['mina', 'fundicion', 'taller', 'lab', 'bomba', 'muelle']);
const ROUTE_KINDS = new Set(['road', 'rail', 'barge', 'boat', 'drone']);

function fail(message: string): never {
  throw new Error(`Isla no válida: ${message}`);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asInt(value: unknown, what: string): number {
  if (typeof value !== 'number' || !Number.isInteger(value)) fail(`${what} debe ser un entero`);
  return value;
}

function asNumber(value: unknown, what: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) fail(`${what} debe ser un número`);
  return value;
}

function asString(value: unknown, what: string): string {
  if (typeof value !== 'string' || value === '') fail(`${what} debe ser un texto`);
  return value;
}

function asArray(value: unknown, what: string): unknown[] {
  if (!Array.isArray(value)) fail(`${what} debe ser una lista`);
  return value;
}

function asCell(value: unknown, what: string): Cell {
  const arr = asArray(value, what);
  if (arr.length !== 2) fail(`${what} debe tener columna y fila`);
  return [asInt(arr[0], `${what}[0]`), asInt(arr[1], `${what}[1]`)];
}

function asPoint(value: unknown, what: string): Point {
  const arr = asArray(value, what);
  if (arr.length !== 2) fail(`${what} debe tener x y z`);
  return [asNumber(arr[0], `${what}[0]`), asNumber(arr[1], `${what}[1]`)];
}

/** Validates the raw JSON of `src/data/island.json` and returns a typed island. */
export function parseIsland(raw: unknown): Island {
  if (!isRecord(raw)) fail('la raíz debe ser un objeto');
  const cols = asInt(raw.cols, 'cols');
  const rows = asInt(raw.rows, 'rows');
  if (cols <= 0 || rows <= 0) fail('cols y rows deben ser positivos');

  if (!isRecord(raw.terrain)) fail('terrain debe ser un objeto');
  const terrain = new Map<string, TerrainCode>();
  for (const [key, code] of Object.entries(raw.terrain)) {
    const [c, r] = parseKey(key);
    if (c < 0 || r < 0 || c >= cols || r >= rows) fail(`la casilla ${key} está fuera de la rejilla`);
    if (typeof code !== 'string' || !TERRAIN_CODES.has(code)) fail(`terreno desconocido en ${key}`);
    terrain.set(cellKey(c, r), code as TerrainCode);
  }
  const onLand = (key: string, what: string): void => {
    const code = terrain.get(key);
    if (!code) fail(`${what} ${key} no es una casilla de la isla`);
  };

  const tiles = asArray(raw.tiles, 'tiles').map((value, i): Tile => {
    if (!isRecord(value)) fail(`tiles[${i}] debe ser un objeto`);
    const kind = asString(value.kind, `tiles[${i}].kind`);
    if (!TILE_KINDS.has(kind)) fail(`tiles[${i}].kind desconocido: ${kind}`);
    const cells = asArray(value.cells, `tiles[${i}].cells`).map((c, j) => asCell(c, `tiles[${i}].cells[${j}]`));
    if (cells.length === 0) fail(`tiles[${i}] no tiene casillas`);
    for (const [c, r] of cells) onLand(cellKey(c, r), `tiles[${i}]`);
    const ports = asArray(value.ports, `tiles[${i}].ports`).map((p, j): Port => {
      const arr = asArray(p, `tiles[${i}].ports[${j}]`);
      const side = asString(arr[1], `tiles[${i}].ports[${j}] lado`);
      const portKind = asString(arr[2], `tiles[${i}].ports[${j}] tipo`);
      if (!SIDES.has(side)) fail(`lado desconocido en tiles[${i}].ports[${j}]`);
      if (portKind !== 'in' && portKind !== 'out') fail(`tipo de puerta desconocido en tiles[${i}].ports[${j}]`);
      return { cell: asCell(arr[0], `tiles[${i}].ports[${j}] casilla`), side: side as Side, kind: portKind };
    });
    const ore = value.ore;
    if (ore !== undefined && ore !== 'iron' && ore !== 'copper') fail(`tiles[${i}].ore desconocido`);
    return {
      id: asString(value.id, `tiles[${i}].id`),
      kind: kind as TileKind,
      name: asString(value.name, `tiles[${i}].name`),
      cells,
      ...(ore ? { ore } : {}),
      product: asString(value.product, `tiles[${i}].product`),
      ports,
    };
  });

  const routes = asArray(raw.routes, 'routes').map((value, i): Route => {
    if (!isRecord(value)) fail(`routes[${i}] debe ser un objeto`);
    const kind = asString(value.kind, `routes[${i}].kind`);
    if (!ROUTE_KINDS.has(kind)) fail(`routes[${i}].kind desconocido: ${kind}`);
    const at = asNumber(value.at, `routes[${i}].at`);
    if (at < 0 || at > 1) fail(`routes[${i}].at debe estar entre 0 y 1`);
    const base = { kind: kind as RouteKind, name: asString(value.name, `routes[${i}].name`), at };
    if (value.pts !== undefined) {
      const pts = asArray(value.pts, `routes[${i}].pts`).map((p, j) => asPoint(p, `routes[${i}].pts[${j}]`));
      if (pts.length < 2) fail(`routes[${i}] necesita al menos dos puntos`);
      return { ...base, pts };
    }
    const curve = asArray(value.curve, `routes[${i}].curve`).map((p, j) => asPoint(p, `routes[${i}].curve[${j}]`));
    const [a, b, c] = curve;
    if (curve.length !== 3 || !a || !b || !c) fail(`routes[${i}].curve necesita tres puntos`);
    return { ...base, curve: [a, b, c] };
  });

  const fog = new Set(asArray(raw.fog, 'fog').map((k, i) => asString(k, `fog[${i}]`)));
  for (const key of fog) onLand(key, 'la niebla de');

  if (!isRecord(raw.locks)) fail('locks debe ser un objeto');
  const locks = new Map<string, number>();
  for (const [key, price] of Object.entries(raw.locks)) {
    onLand(key, 'el candado de');
    const value = asInt(price, `locks[${key}]`);
    if (value <= 0) fail(`el precio de ${key} debe ser positivo`);
    locks.set(key, value);
  }

  const buyTarget = asString(raw.buyTarget, 'buyTarget');
  onLand(buyTarget, 'la casilla a comprar');
  if (!locks.has(buyTarget)) fail('la casilla a comprar debe tener candado');
  const goal = asCell(raw.goal, 'goal');
  onLand(cellKey(...goal), 'la meta');

  return { cols, rows, terrain, tiles, routes, fog, locks, buyTarget, goal };
}
